import { composeThemeFromProps, Compose } from '@css-modules-theme/react';
import clsx from 'clsx';
import { forwardRef, useRef } from 'react';
import mergeRefs from 'react-merge-refs';
import defaultStyles from './styles/VendorButton.module.scss.js';
import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import SvgGooglePayColour from './assets/GooglePayColour.js';
import SvgFacebookMono from './assets/FacebookMono.js';
import SvgGooglePlusMono from './assets/GooglePlusMono.js';
import SvgAmazonPay from './assets/AmazonPay.js';
import SvgApplePayColour from './assets/ApplePayColour.js';

const VendorButton = /*#__PURE__*/forwardRef(({
  href,
  presetIcon,
  disabled = false,
  fluid = false,
  dataTestId,
  svgWidth,
  svgHeight,
  theme,
  Component = href ? 'a' : 'button',
  Icon,
  ...rest
}, ref) => {
  const buttonRef = useRef(null);
  const styles = composeThemeFromProps(defaultStyles, {
    theme
  }, {
    compose: Compose.Merge
  });
  const vendorStyles = clsx({
    [styles.applePay]: presetIcon === 'applePay',
    [styles.googlePay]: presetIcon === 'googlePay',
    [styles.amazonPay]: presetIcon === 'amazonPay',
    [styles.googleSocial]: presetIcon === 'googleSocial',
    [styles.facebookSocial]: presetIcon === 'facebookSocial'
  });
  const vendorButtonProps = {
    className: clsx(styles.root, fluid && styles.fluid, vendorStyles, disabled && styles.disabled),
    'data-test-id': dataTestId || null,
    ref,
    ...rest
  };
  let svgProps;
  if (svgWidth) {
    svgProps.svgWidth = svgWidth;
  }
  if (svgHeight) {
    svgProps.svgHeight = svgHeight;
  }
  let ButtonIcon = Icon;
  switch (presetIcon) {
    case 'applePay':
      ButtonIcon = /*#__PURE__*/jsxs(Fragment, {
        children: ["Pay With ", /*#__PURE__*/jsx(SvgApplePayColour, {
          ...svgProps
        })]
      });
      break;
    case 'amazonPay':
      ButtonIcon = /*#__PURE__*/jsx(SvgAmazonPay, {
        ...svgProps
      });
      break;
    case 'googleSocial':
      ButtonIcon = /*#__PURE__*/jsxs(Fragment, {
        children: [/*#__PURE__*/jsx(SvgGooglePlusMono, {
          ...svgProps
        }), " Google"]
      });
      break;
    case 'facebookSocial':
      ButtonIcon = /*#__PURE__*/jsxs(Fragment, {
        children: [/*#__PURE__*/jsx(SvgFacebookMono, {
          ...svgProps
        }), " Facebook"]
      });
      break;
    default:
      ButtonIcon = /*#__PURE__*/jsxs(Fragment, {
        children: ["Pay With ", /*#__PURE__*/jsx(SvgGooglePayColour, {
          ...svgProps
        })]
      });
      break;
  }
  const vendorButtonChildren = /*#__PURE__*/jsx("span", {
    className: styles.icon,
    children: Icon ?? ButtonIcon
  });
  if (href) {
    const {
      target
    } = rest;
    const hrefProps = {
      [typeof Component === 'string' ? 'href' : 'to']: href
    };
    return /*#__PURE__*/jsx(Component, {
      rel: target === '_blank' ? 'noreferrer noopener' : undefined,
      ...vendorButtonChildren,
      ...rest,
      ...hrefProps,
      ref: mergeRefs([buttonRef, ref]),
      children: vendorButtonChildren
    });
  }
  const {
    type = 'button',
    ...restProps
  } = rest;
  return /*#__PURE__*/jsx(Component, {
    ...vendorButtonProps,
    ...restProps,
    type: type,
    disabled: disabled,
    ref: mergeRefs([buttonRef, ref]),
    children: vendorButtonChildren
  });
});
VendorButton.displayName = 'VendorButton';

export { VendorButton };
